import { ReactComponent as IconFolderShow } from '@/assets/svgs/chevron-down-medium.svg'
import { assetUrl } from '@/utils/asset-url'
import { ReactComponent as IconChevronDown } from '@/assets/svgs/chevron-down.svg'
import { ReactComponent as IconFolderHide } from '@/assets/svgs/chevron-up-medium.svg'
import { ReactComponent as IconEclip } from '@/assets/svgs/eclip-large.svg'
import { ReactComponent as IconFolder } from '@/assets/svgs/folder-large.svg'
import { ReactComponent as IconGridView } from '@/assets/svgs/grid-view.svg'
import { ReactComponent as IconSearch } from '@/assets/svgs/icon-search.svg'
import { ReactComponent as IconTableView } from '@/assets/svgs/table-view.svg'
import Dropdown from '@/components/Dropdown'
import InputSuffixIcon from '@/components/InputSuffixIcon'
import Portal from '@/components/Portal/index.js'
import { sortByOptions } from '@/constants/index'
import { ToastContext } from '@/context/toast/index'
import usePackageFolders from '@/hooks/use-package-folder/index.js'
import usePackages from '@/hooks/use-packages/index.js'
import usePage from '@/hooks/use-page/index.js'
import usePaginating from '@/hooks/use-paginating/index.js'
import { useLocation } from '@/lib/router-dom'
import api from "@/services/api/packageFolder.js"
import rem from '@/utils/rem.js'
import { useContext, useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import PackageCard from './package-card.jsx'
import PackageDataTable from './package-data-table.jsx'
import FilterDropDown from './package-filter-dropdown.jsx'
import PackageFolderCard from './package-folder-card.jsx'
import PackageFolderDataTable from './package-folder-data-table.jsx'
import Search from '@/componentsDS/Search/index';
import { feature, features } from "@/utils/feature-verifier";

export default function Packages() {

	/*=================================================
		General
	/================================================*/

	const folderPageLimit = 8;
	const packagePageLimit = 10;
	const { currentPath } = useLocation()
	const { addToastMessage } = useContext(ToastContext)

	const [isListView, setIsListView] = useState(false)
	const [refIdSearch, setRefIdSearch] = useState('')
	const [dataElement, setDataElement] = useState({})
	const [participant, setParticipant] = useState({})
	const [creator, setCreator] = useState({})
	const [status, setStatus] = useState({})
	const [fromDate, setFromDate] = useState(null)
	const [endDate, setEndDate] = useState(null)
	const [caseOwner, setCaseOwner] = useState('')
	const [showFolders, setShowFolders] = useState(true)
	const [showPackages, setShowPackages] = useState(true)
	const [refreshData, setRefreshData] = useState(false)

	/*=================================================
		Hooks
	/================================================*/

	const {
		packages,
		packagesMeta,
		fetchAllPackages,
		packageLoading,
		setPackageLoading,
		packageErrorMsg,
		getFilterOptions,
		filterDataElement,
		filterParticipant,
		filterStatus,
		filterCreator
	} = usePackages()

	const {
		folders,
		foldersMeta,
		fetchAllFolders,
		folderLoading,
		folderErrorMsg
	} = usePackageFolders()

	const { breadcrumbsItems } = usePage({
		title: 'Package Sharing',
		breadcrumbs: ['Package Sharing'],
	})

	const {
		itemsPerPage: itemsPerPagePackages,
		currentPage: currentPackagesPage,
		goToPrev: goToPrevPackagesPage,
		goToNext: goToNextPackagesPage,
		handleCurrnetInput: handlePackagePageInput,
		sortConfig: sortPackage,
		setSortConfig: setSortPackage,
	} = usePaginating(
		packagePageLimit,
		packagesMeta?.total,
		packagesMeta?.totalPages,
	)

	const {
		itemsPerPage: itemsPerPageFolders,
		currentPage: currentPackageFoldersPage,
		goToPrev: goToPrevFoldersPage,
		goToNext: goToNextFoldersPage,
		handleCurrnetInput: handleFolderPageInput,
		sortConfig: sortPackageFolders,
		setSortConfig: setSortPackageFolders,
	} = usePaginating(
		folderPageLimit,
		foldersMeta?.total,
		foldersMeta?.totalPages,
	)

	const handleClearFilter = () => {
		setRefIdSearch("")
		setDataElement({})
		setParticipant({})
		setStatus({})
		setCreator({})
		setFromDate("")
		setEndDate("")
		setCaseOwner("")
	}

	const toggleFoldersClick = () => {
		setShowFolders(!showFolders)
	}

	const togglePackagesClick = () => {
		setShowPackages(!showPackages)
	}

	const init = () => {
		fetchAllFolders({
			sort_order: sortPackageFolders.direction ? 'ASC' : 'DESC',
			sort_by: sortPackageFolders.key,
			limit: itemsPerPageFolders,
			page: currentPackageFoldersPage,
		})
		fetchAllPackages({
			sort_by: sortPackage.key,
			sort_order: sortPackage.direction ? 'ASC' : 'DESC',
			data_element_ids: dataElement?.dataElementId || undefined,
			participants: participant?.orgId || undefined,
			reference_number: refIdSearch || undefined,
			status: status?.id || undefined,
			limit: itemsPerPagePackages,
			page: currentPackagesPage,
			from_date: fromDate ? fromDate.toISOString() : undefined,
			to_date: endDate ? endDate.toISOString() : undefined,
			createdByOrg: creator?.orgId || undefined,
			// send_owner: path.includes('shared') ? undefined : caseOwner?.email || undefined,
			// receive_owner: path.includes('shared') ? caseOwner?.email || undefined : undefined,
		})
		getFilterOptions()
		setIsListView(localStorage.getItem('isListView') === 'true')
	}

	useEffect(() => {
		handlePackagePageInput({
			target: {
				value: 1
			}
		})
		handleFolderPageInput({
			target: {
				value: 1
			}
		})
	}, [currentPath])

	useEffect(init, [
		currentPath,
		sortPackageFolders,
		sortPackage,
		dataElement,
		participant,
		creator,
		refIdSearch,
		status,
		currentPackageFoldersPage,
		fromDate,
		endDate,
		caseOwner,
		currentPackagesPage,
		refreshData
	])

	const movePackageToFolder = async (id, payload) => {
		const [error] = await api.movePackage(id, payload);
		if (error) {
			return addToastMessage('Failed to move the package', 'error')
		}
		init()
	}

	useEffect(() => {
		if (folderErrorMsg) {
			addToastMessage(folderErrorMsg, 'error')
		}
	}, [folderErrorMsg])

	useEffect(() => {
		if (packageErrorMsg) {
			addToastMessage(packageErrorMsg, 'error')
		}
	}, [packageErrorMsg])

	const handleToggleTabView = () => {
		setIsListView(!isListView);
		isListView ? localStorage.setItem("isListView", "false") : localStorage.setItem("isListView", "true")
	};

	return (
		<>
			<div className="eclip-landing__filter overview">
				<div hidden onClick={init} onKeyDown={init} id="reload-eclips"></div>
				<div className="eclip-filter option-position">
					<div className="search-data-input ">
						<div style={{width:"350px"}}>
							<Search
								value={refIdSearch}
								placeholder="Search by Reference ID"
								onChange={(e) => setRefIdSearch(e?.target.value)}
								onCancel={() => setRefIdSearch('')}
							/>
						</div>

						<FilterDropDown
							setFromDate={setFromDate}
							setEndDate={setEndDate}
							statusFilterList={filterStatus}
							setStatus={setStatus}
							participantsFilterList={filterParticipant}
							setParticipant={setParticipant}
							dataElementsFilterList={filterDataElement}
							setDataElement={setDataElement}
							creatorFilterList={filterCreator}
							setCreator={setCreator}
							handleClearFilter={handleClearFilter}
						/>
					</div>
					<div className="eclip-landing__view-switch">
						{!isListView && (
							<div className="search-data-input__sort-by">
								<span className="sort-by">Sort By</span>
								<Dropdown
									placeholder="Sort By"
									value={
										sortByOptions.find(
											(option) =>
												option.direction ===
												sortPackage.direction
										)?.name
									}
									style={{ width: rem(130), zIndex: 1 }}
								>
									{sortByOptions.map((option, index) => {
										return (
											<Dropdown.Item key={index}
												onClick={() => {
													setSortPackage({
														direction: option.direction,
														key: option.key,
													});
													setSortPackageFolders({
														direction: option.direction,
														key: option.key,
													});
												}}
												id={option.key}
											>
												{option.name}
											</Dropdown.Item>
										);
									})}
								</Dropdown>
							</div>
						)}
						<div
							data-testid="toggle-table-view"
							onClick={handleToggleTabView}
							onKeyDown={handleToggleTabView}
						>
							{
								isListView ?
									<div className="package-lading__layout-view tooltip">
										<span className="tooltiptext__package-landing">
											Grid Layout
										</span>
										<IconGridView />
									</div>
									: <div className="package-lading__layout-view tooltip">
										<span className="tooltiptext__package-landing">
											List Layout
										</span>
										<IconTableView />
									</div>
							}
						</div>
					</div>
				</div>
			</div>

			<div className="package-landing__search-msg">
				{refIdSearch && <div className='package-landing__search-msg-text'>{`Showing results for "${refIdSearch}"`}</div>}
			</div>

			<div>
				<div className="eclip-landing__heading-group">
					<div
						className="package-landing__sub-heading"
						onClick={toggleFoldersClick}
						onKeyDown={toggleFoldersClick}
						data-testid="toggle-folder">
						{showFolders && (
							<>
								<div>Folders ({foldersMeta?.total || 0})</div>
								<div className="tooltip">
									<span className="tooltiptext__package-landing">
										Hide Folder
									</span>
									<IconFolderHide />
								</div>
							</>
						)}
						{!showFolders && (
							<>
								<div > Folders ({foldersMeta?.total || 0}) </div>
								<div className="tooltip">
									<span className="tooltiptext__package-landing">
										Show Folder
									</span>
									<IconFolderShow />
								</div>
							</>
						)}
					</div>
					<div className="eclip-landing__pagination">
						{showFolders && foldersMeta?.total ?
							<div className="data-table__table__footer__row">
								<div className="data-table__table__footer__row__right">
									<div className="data-table__table__pagination">
										<input
											className="data-table__table__pagination__page"
											type="number"
											value={currentPackageFoldersPage}
											onChange={(e) => handleFolderPageInput(e)}
											min="1"
										/>
										<span className="data-table__table__pagination__page__meta">
											{" "}
											of{" "}
											{foldersMeta?.totalPages || 1}{" "}
											pages{" "}
										</span>
										<button
											className="data-table__table__pagination__prev"
											onClick={() => goToPrevFoldersPage()}
											disabled={currentPackageFoldersPage === 1 || !foldersMeta?.totalPages}
										>
											<IconChevronDown />
										</button>
										<button
											className="data-table__table__pagination__next"
											onClick={() => goToNextFoldersPage()}
											disabled={
												currentPackageFoldersPage === foldersMeta?.totalPages ||
												!foldersMeta?.totalPages
											}
										>
											<IconChevronDown />
										</button>
									</div>
								</div>
							</div>
							: <div></div>}
					</div>
				</div>
				{showFolders && isListView && (
					<div className="eclip-landing__data-table">
						<PackageFolderDataTable
							items={folders}
							sortConfig={sortPackageFolders}
							setSortConfig={setSortPackageFolders}
							refreshData={refreshData}
							setRefreshData={setRefreshData}
						/>
					</div>
				)}
				{showFolders && !isListView && (
					<div>
						{folders?.length > 0 ? (
							<div className="package-landing__folder-wrapper">
								{folders?.map((item) => {
									return (
										<PackageFolderCard
											key={item.id}
											item={item}
											refreshData={refreshData}
											setRefreshData={setRefreshData}
											moveToFolder={movePackageToFolder}
										/>
									);
								})}
							</div>
						) : (
							<div className="data-table__table__body">
								<div className="data-table__empty">
									<div className="data-table__empty__icon">
										<IconFolder />
										<span>
											Click{" "}
											<b>Create New Folders</b> to
											organize your Eclips easily.
										</span>
									</div>
								</div>
							</div>
						)}
					</div>
				)}
				{!showFolders && (<div className="eclip-landing-folder--wrapper"></div>)}

				<div className="eclip-landing__heading-group">
					<div
						className="package-landing__sub-heading"
						onClick={togglePackagesClick}
						onKeyDown={togglePackagesClick}
						data-testid="toggle-package">
						{showPackages && (
							<>
								<div>Packages ({packagesMeta?.total || 0})</div>
								<div className="tooltip">
									<span className="tooltiptext__package-landing">
										Hide Package
									</span>
									<IconFolderHide />
								</div>
							</>
						)}
						{!showPackages && (
							<>
								<div > Packages ({packagesMeta?.total || 0}) </div>
								<div className="tooltip">
									<span className="tooltiptext__package-landing">
										Show Package
									</span>
									<IconFolderShow />
								</div>
							</>
						)}
					</div>
					<div className="eclip-landing__pagination">
						{showPackages && packagesMeta?.total ? (<div className="data-table__table__footer__row">
							<div className="data-table__table__footer__row__right">
								<div className="data-table__table__pagination">
									<input
										className="data-table__table__pagination__page"
										type="number"
										value={currentPackagesPage}
										onChange={(e) => handlePackagePageInput(e)}
										min="1"
									/>
									<span className="data-table__table__pagination__page__meta">
										{" "}
										of {packagesMeta?.totalPages ||
											1}{" "}
										pages{" "}
									</span>
									<button
										className="data-table__table__pagination__prev"
										onClick={() => goToPrevPackagesPage()}
										disabled={
											currentPackagesPage === 1 ||
											!packagesMeta?.totalPages
										}
									>
										<IconChevronDown />
									</button>
									<button
										className="data-table__table__pagination__next"
										onClick={() => goToNextPackagesPage()}
										disabled={
											currentPackagesPage ===
											packagesMeta?.totalPages ||
											!packagesMeta?.totalPages
										}
									>
										<IconChevronDown />
									</button>
								</div>
							</div>
						</div>
						) : (<div></div>)}
					</div>
				</div>
				{showPackages && isListView && (
					<div className="eclip-landing__data-table">
						<PackageDataTable
							items={packages}
							sortConfig={sortPackage}
							setLoading={setPackageLoading}
							setSortConfig={setSortPackage}
							refreshData={refreshData}
							setRefreshData={setRefreshData}
						/>
					</div>
				)}
				{showPackages && !isListView && (
					<div>
						{packages?.length > 0 ? (
							<div className="package-landing__package-card--wrapper">
								{packages?.map((item) => {
									return (
										<PackageCard
											key={item.id}
											item={item}
											refreshData={refreshData}
											setRefreshData={setRefreshData}
											showCheckBox={false}
										/>
									);
								})}
							</div>
						) : (
							<div className="data-table__table__body">
								<div className="data-table__empty">
									<div className="data-table__empty__icon">
										<IconEclip />
										You haven’t created any Packages.
									</div>
								</div>
							</div>
						)}
					</div>
				)}
				{!showPackages && (<div className="package-landing__package-card--wrapper"></div>)}
			</div>
			{(folderLoading || packageLoading) &&
				ReactDOM.createPortal(
					<div className="modal white">
						<div className="loading">
							<img src={assetUrl("/loading.gif")} />
						</div>
					</div>,
					document.body
				)}
		</>
	);
}
